import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AddPersonDTO,
  AssignmentUpdateOk,
  InfoDeleteDTO,
  InfoGetDTO,
  InfoGetOkDTO,
  InfoState,
  UpdateOkDTO,
  updateUserRolesBulkDTO,
  updateUserRolesBulkResponse,
} from './types';
import { Invitation, RespondInvitationDTO } from '../recruitment/types';
import { IgnoreConflict } from '../bid/types';

const initialState: InfoState = {
  info: {
    users: {
      byId: {},
      personRoleIdMap: {},
    },
    invitations: {
      byId: {},
    },
    reviewers_bids: {
      byId: {},
    },
    reviewer_bids: {
      byId: {},
    },
    submissions_bids: {
      byId: {},
    },
    submission_bids: {
      byId: {},
    },
    reviews_to_papers: {
      byId: {},
    },
    reviewer_assignments: {
      byId: {},
    },
    papers_to_reviews: {
      byId: {},
    },
    submission_assignments: {
      byId: {},
    },
    reviewers_to_reviewers: {
      byId: {},
      selectorValues: {
        fromRoleId: 0,
        toRoleId: 0,
      },
    },
    reviewer_assignments_of_reviewers: {
      byId: {},
    },
    submissions_to_review: {
      byId: {},
    },
    people_to_review: {
      byId: {},
    },
  },
  loading: false,
  loadingAction: false,
};

const infoSlice = createSlice({
  name: 'INFO',
  initialState,
  reducers: {
    reset: () => initialState,
    ['LOADING:ON']: (state) => ({
      ...state,
      loading: true,
    }),
    ['LOADING:OFF']: (state) => ({
      ...state,
      loading: false,
    }),
    ['GET']: (state, _action: PayloadAction<InfoGetDTO>) => ({
      ...state,
      loading: true,
    }),
    ['GET:OK']: (state, action: PayloadAction<InfoGetOkDTO>) => {
      let personRoleIdMap;
      if (action.payload.friendlyName == 'users') {
        personRoleIdMap = {};
        Object.keys(action.payload.byId).forEach((key: string) => {
          const person = action.payload.byId[parseInt(key)];
          person.person_role_ids.forEach((person_role_id: number) => {
            personRoleIdMap[person_role_id] = parseInt(key);
          });
        });
      } else {
        personRoleIdMap = undefined;
      }

      return {
        ...state,
        loading: false,
        info: {
          ...state.info,
          [action.payload.friendlyName]: {
            ...state.info[action.payload.friendlyName],
            byId: action.payload.byId,
            personRoleIdMap: personRoleIdMap,
          },
        },
      };
    },
    ['GET:KO']: (state) => ({
      ...state,
      loading: false,
    }),
    ['DELETE']: (state, action: PayloadAction<InfoDeleteDTO>) => ({
      ...state,
      info: {
        ...state.info,
        [action.payload.friendlyName]: initialState.info[action.payload.friendlyName],
      },
    }),
    ['UPDATE:OK']: (state, action: PayloadAction<UpdateOkDTO>) => {
      const { tableFriendlyName, byId } = action.payload;
      return {
        ...state,
        info: {
          ...state.info,
          [tableFriendlyName]: {
            byId,
          },
        },
      };
    },
    ['USER:UPDATE:ROLES:BULK']: (state, action: PayloadAction<updateUserRolesBulkDTO>) => ({
      ...state,
    }),
    ['USER:UPDATE:ROLES:BULK:OK']: (state, action: PayloadAction<updateUserRolesBulkResponse>) => {
      const newById = { ...state.info.users.byId };
      const newPersonRoleIdMap = { ...state.info.users.personRoleIdMap };

      Object.entries(action.payload.data).forEach(([idStr, value]) => {
        const personId = parseInt(idStr);
        newById[personId] = { ...newById[personId], role_ids: value.role_ids, person_role_ids: value.person_role_ids };
        value.person_role_ids.forEach((personRoleId) => {
          newPersonRoleIdMap[personRoleId] = personId;
        });
      });

      return {
        ...state,
        info: {
          ...state.info,
          users: {
            ...state.info.users,
            byId: newById,
            personRoleIdMap: newPersonRoleIdMap,
          },
        },
      };
    },
    ['DELETE_INVITATIONS:OK']: (state, action: PayloadAction<number[]>) => {
      const newById: { [key: number]: Invitation } = {};
      Object.keys(state.info.invitations.byId)
        .filter((key) => !action.payload.includes(parseInt(key)))
        .forEach((key) => {
          const idx = parseInt(key);
          newById[idx] = state.info.invitations.byId[idx];
        });
      return {
        ...state,
        loading: false,
        info: { ...state.info, invitations: { ...state.info.invitations, byId: newById } },
      };
    },
    ['RESPOND_INVITATION:OK']: (state, action: PayloadAction<RespondInvitationDTO>) => {
      return {
        ...state,
        loading: false,
        info: {
          ...state.info,
          invitations: {
            ...state.info.invitations,
            byId: {
              ...state.info.invitations.byId,
              [action.payload.id]: {
                ...state.info.invitations.byId[action.payload.id],
                ...action.payload,
              },
            },
          },
        },
      };
    },
    ['USER:ADD']: (state, action: PayloadAction<AddPersonDTO>) => ({
      ...state,
      loadingAction: true,
    }),
    ['USER:ADD:OK']: (state) => ({
      ...state,
      loadingAction: false,
    }),
    ['USER:ADD:KO']: (state) => ({
      ...state,
      loadingAction: false,
    }),
    ['ASSIGNMENTS:SET_SELECTOR_VALUES']: (state, action: PayloadAction<{ fromRoleId: number; toRoleId: number }>) => ({
      ...state,
    }),
    ['ASSIGNMENTS:SET_SELECTOR_VALUES:OK']: (
      state,
      action: PayloadAction<{ fromRoleId: number; toRoleId: number }>,
    ) => ({
      ...state,
      info: {
        ...state.info,
        reviewers_to_reviewers: {
          ...state.info.reviewers_to_reviewers,
          selectorValues: action.payload,
        },
      },
    }),
    ['ASSIGNMENTS:UPDATE:OK']: (state, action: PayloadAction<AssignmentUpdateOk>) => {
      const { assignment_id, completed, modelId, tableFriendlyName } = action.payload;
      const assigned = assignment_id > 0;
      return {
        ...state,
        info: {
          ...state.info,
          [tableFriendlyName]: {
            ...state.info[tableFriendlyName],
            byId: {
              ...state.info[tableFriendlyName].byId,
              [modelId]: {
                ...state.info[tableFriendlyName].byId[modelId],
                id: assigned ? assignment_id : null,
                assigned,
                completed,
                ignored: assigned && state.info[tableFriendlyName].byId[modelId].conflicts,
              },
            },
          },
        },
      };
    },
    ['ASSIGNMENTS:IGNORE_CONFLICT:OK']: (state, action: PayloadAction<IgnoreConflict>) => {
      const { assignment_id, tableFriendlyName } = action.payload;

      const findKey = Object.keys(state.info[tableFriendlyName].byId).find(
        (key) => state.info[tableFriendlyName].byId[key].id == assignment_id,
      ) as string;

      return {
        ...state,
        info: {
          ...state.info,
          [tableFriendlyName]: {
            ...state.info[tableFriendlyName],
            byId: {
              ...state.info[tableFriendlyName].byId,
              [findKey]: {
                ...state.info[tableFriendlyName].byId[findKey],
                ignored: true,
              },
            },
          },
        },
      };
    },
  },
});

export default infoSlice;
